import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS, groupPermissions } from "@util/permissions";

export default function AdminRolesPermissions({ adminId }) {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    fetchData();
  }, [adminId]);

  function fetchData() {
    setLoading(true);
    Promise.all([
      axios.post(`/admins/${adminId}/retrieve-roles-permissions/`, {}),
      axios.get(`/roles/`),
      axios.post(`/retrieve-permissions/`, {
        scope: "all",
        page: 0,
        page_size: 500,
      }),
    ])
      .then(([assignedRes, rolesRes, catalogRes]) => {
        const catalogById = Object.fromEntries(
          (catalogRes.data.response[0]?.data || []).map((permission) => [
            String(permission.id),
            permission,
          ])
        );
        const assigned = (assignedRes.data.response[0]?.roles || []).map(
          (role) => ({
            ...role,
            permissions: (role.permissions || []).map((permission) => ({
              ...permission,
              key: permission.key || catalogById[String(permission.id)]?.key,
              group:
                permission.group || catalogById[String(permission.id)]?.group,
            })),
          })
        );
        setRoles(assigned);
        const assignedIds = new Set(assigned.map((r) => String(r.id)));
        setAvailableRoles(
          (rolesRes.data.response || [])
            .filter((r) => !assignedIds.has(String(r.id)))
            .map((r) => ({ value: String(r.id), label: r.name }))
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function addRole() {
    if (!selectedRoleId) return;
    setBusyKey(`role-add-${selectedRoleId}`);
    axios
      .post(`/admins/${adminId}/roles/`, { role_id: selectedRoleId })
      .then(() => {
        setSelectedRoleId(null);
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function removeRole(roleId) {
    setBusyKey(`role-remove-${roleId}`);
    axios
      .post(`/admins/${adminId}/remove-role/`, { role_id: roleId })
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function togglePermission(permission, added) {
    setBusyKey(`perm-${permission.id}`);
    const request = added
      ? axios.post(`/admins/${adminId}/remove-permission/`, {
          permission_id: permission.id,
        })
      : axios.post(`/admins/${adminId}/permissions/`, {
          permission_id: permission.id,
        });

    request
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Can
        permission={[
          PERMISSIONS.ROLES_ASSIGN,
          PERMISSIONS.ADMINS_ASSIGN_MASTER,
        ]}
        mode="any"
      >
        <Card>
          <Title order={5} mb="sm">
            {t("add")} {t("role_singular")}
          </Title>
          <Flex gap="sm" align="flex-end">
            <Select
              style={{ flex: 1 }}
              searchable
              clearable
              placeholder={t("role_singular")}
              data={availableRoles}
              value={selectedRoleId}
              onChange={setSelectedRoleId}
            />
            <Button
              leftSection={<IconPlus size={14} />}
              disabled={!selectedRoleId}
              loading={busyKey === `role-add-${selectedRoleId}`}
              onClick={addRole}
            >
              {t("add")}
            </Button>
          </Flex>
        </Card>
      </Can>

      {!roles.length && <EmptyMessage />}

      {roles.map((role) => {
        const sections = groupPermissions(role.permissions || []);

        return (
          <Card key={role.id}>
            <Flex gap="sm" align="center" mb="sm">
              <Text fw={700} style={{ flexGrow: 1 }}>
                {role.name}
              </Text>
              <Can
                permission={[
                  PERMISSIONS.ROLES_ASSIGN,
                  PERMISSIONS.ADMINS_ASSIGN_MASTER,
                ]}
                mode="any"
              >
                <ActionIcon
                  size="sm"
                  radius="xl"
                  variant="light"
                  color="red"
                  title={t("remove")}
                  loading={busyKey === `role-remove-${role.id}`}
                  onClick={() => removeRole(role.id)}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Can>
            </Flex>

            <Stack gap="md">
              {sections.map((section, index) => (
                <Stack key={section.key} gap="xs">
                  {index > 0 && <Divider />}
                  <Text size="xs" tt="uppercase" c="dimmed" fw={700}>
                    {section.label}
                  </Text>
                  {section.permissions.map((permission) => (
                    <Flex key={permission.id} gap="sm" align="center">
                      <div style={{ flexGrow: 1 }}>
                        <Group gap="xs">
                          <Text size="sm">{permission.name}</Text>
                          {permission.default && (
                            <Badge size="xs" variant="light" color="blue">
                              {t("default")}
                            </Badge>
                          )}
                        </Group>
                      </div>
                      <Can
                        permission={[
                          PERMISSIONS.ADMINS_MANAGE_OWN_PERMISSIONS,
                          PERMISSIONS.ADMINS_MANAGE_OTHER_PERMISSIONS,
                        ]}
                        mode="any"
                      >
                        <Switch
                          size="sm"
                          checked={!!permission.added}
                          disabled={busyKey === `perm-${permission.id}`}
                          onChange={() =>
                            togglePermission(permission, permission.added)
                          }
                        />
                      </Can>
                    </Flex>
                  ))}
                </Stack>
              ))}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}
